import { Component, Input, ViewChild, SimpleChanges } from "@angular/core";
import { LatLngBoundsLiteral, AgmMap } from "@agm/core";
import * as moment from "moment";

@Component({
    selector: "gmap",
    template: `
    <agm-map
    (mapReady)="mapReadyEvent($event)"
        [latitude]="lat"
        [longitude]="lng"
        [zoom]="zoom"
        [styles]="styles">
        <agm-direction  *ngFor="let d of dir; let i = index"
            [origin]="d.origin"
            [destination]="d.destination"
            [waypoints]="d.waypoints"
            [renderOptions]="renderOptions">
        </agm-direction>
        <agm-marker *ngFor="let m of markers; let i = index"
            (markerClick)="clickedMarker(m.label, infoWindow, i)"
            [latitude]="m.lat"
            [longitude]="m.lng"
            [iconUrl]="m.iconUrl">
            <agm-info-window #infoWindow>
                <h4 [innerHTML]="m.label"></h4>
                <p [innerHTML]="m.content"></p>
            </agm-info-window>
        </agm-marker>
    </agm-map>
    <div class="map-warning">
    {{('MapNotice' | translate) | async }}
    </div>`,
    styles: [
        `
            .alert--wrap {
                word-wrap: break-word;
            }
            .agm-info-window-content {
                max-width: 300px;
            }
            agm-map {
                height: 300px;
                width: 100%;
            }
        `
    ]
})
export class GoogleMapComponent {
    @Input("index") index: string;
    @Input("route") route: string;
    styles = [
        {
            featureType: "landscape",
            elementType: "all",
            stylers: [
                {
                    saturation: 43.400000000000006
                },
                {
                    lightness: 37.599999999999994
                },
                {
                    gamma: 1
                },
                {
                    color: "#fef2e2"
                }
            ]
        },
        {
            featureType: "landscape.man_made",
            elementType: "all",
            stylers: [
                {
                    color: "#fccb8b"
                }
            ]
        },
        {
            featureType: "poi",
            elementType: "all",
            stylers: [
                {
                    saturation: -1.0989010989011234
                },
                {
                    lightness: 11.200000000000017
                },
                {
                    gamma: 1
                },
                {
                    color: "#fee5c5"
                }
            ]
        },
        {
            featureType: "road.highway",
            elementType: "all",
            stylers: [
                {
                    hue: "#FFC200"
                },
                {
                    saturation: -61.8
                },
                {
                    lightness: 45.599999999999994
                },
                {
                    gamma: 1
                }
            ]
        },
        {
            featureType: "road.arterial",
            elementType: "all",
            stylers: [
                {
                    hue: "#ff0300"
                },
                {
                    saturation: -100
                },
                {
                    lightness: 51.19999999999999
                },
                {
                    gamma: 1
                }
            ]
        },
        {
            featureType: "road.local",
            elementType: "all",
            stylers: [
                {
                    hue: "#FF0300"
                },
                {
                    saturation: -100
                },
                {
                    lightness: 52
                },
                {
                    gamma: 1
                }
            ]
        },
        {
            featureType: "water",
            elementType: "all",
            stylers: [
                {
                    saturation: "0"
                },
                {
                    lightness: "0"
                },
                {
                    gamma: "0.80"
                },
                {
                    color: "#30c0cb"
                }
            ]
        }
    ];

    @ViewChild(AgmMap) agmMap: AgmMap;

    infoWindowOpened = null;

    lat = 0;
    lng: number = 0;
    dir = [];
    zoom = 5;
    markers: marker[];
    bounds: point;
    positioned = false;
    mapReady = false;
    markersToAdd: marker[] = [];
    initialised = false;

    renderOptions = {
        suppressMarkers: true
    };

    public focusPin(pin: marker, zoom: number) {
        this.lat = pin.lat;
        this.lng = pin.lng;
        this.zoom = zoom;
    }

    public addPin(pin: marker) {
        if (!this.mapReady) {
            this.markersToAdd.push(pin);
        } else {
            if (pin.lat != null && pin.lat !== 0) {
                this.markers.push(pin);
                this.refreshMap();
            }
        }
    }

    ngOnChanges(changes: SimpleChanges) {
        this.dir = [];
        this.markers = [];
        this.ngOnInit();
        this.refreshMap();
    }

    mapReadyEvent(event: any) {
        this.mapReady = true;
        this.markersToAdd.forEach(marker => {
            this.addPin(marker);
        });
    }

    clickedMarker(label: string, infoWindow, index: number) {
        if (this.infoWindowOpened === infoWindow) {
            return;
        }

        if (this.infoWindowOpened !== null) {
            this.infoWindowOpened.close();
        }

        this.infoWindowOpened = infoWindow;
    }

    refreshMap() {
        this.agmMap.fullscreenControl = true;
        this.agmMap.triggerResize();
        this.agmMap.fitBounds = this.bounds;
    }

    ngOnInit() {
        if (this.initialised) {
            return;
        }
        this.bounds = new point();
        const waypointList = [];
        function addToWaypoints(stop, index) {
            const lat = stop.StopDetails[0].Latitude;
            const long = stop.StopDetails[0].Longitude;
            if (index <= 20 && lat !== 0 && long !== 0) {
                waypointList.push({
                    location: lat + "," + long,
                    stopover: true
                });
            }
        }

        function chunkArray(myArray, chunk_size) {
            const arrayLength = myArray.length;
            const tempArray = [];

            for (let index = 0; index < arrayLength; index += chunk_size) {
                const myChunk = myArray.slice(index, index + chunk_size + 1);
                tempArray.push(myChunk);
            }

            return tempArray;
        }

        const pins: marker[] = [];
        this.bounds.north = -90;
        this.bounds.south = 90;
        this.bounds.east = -180;
        this.bounds.west = 180;

        const _bounds = this.bounds;

        function addToMap(stop, index, stopList) {
            const addressNumber = stop.StopDetails[0].AddressNumber;
            const lat =
                stop.StopDetails[0].Addresses[addressNumber - 1].Latitude;
            const long =
                stop.StopDetails[0].Addresses[addressNumber - 1].Longitude;
            const pin = new marker();
            pin.lat = lat;
            pin.lng = long;
            pin.iconUrl = "/assets/icons/map/stop.png";

            if (lat > _bounds.north) {
                _bounds.north = lat;
            }
            if (lat < _bounds.south) {
                _bounds.south = lat;
            }

            if (long > _bounds.east) {
                _bounds.east = long;
            }

            if (long < _bounds.west) {
                _bounds.west = long;
            }

            const restStopText = "";
            let restStopIcon = "";
            if (stop.IsRestStop === true) {
                pin.iconUrl = "/assets/icons/map/rest-stop.png";
                restStopIcon = '<i class="fa fa-cutlery"></i>';
                // restStopText = "\<br\/\> This is a rest stop. Grab a bite!";
                pin.label =
                    " Arrives " +
                    moment(stop.ArrivalDate).format("HH:mm") +
                    " - Departs " +
                    moment(stop.DepartureDate).format("HH:mm") +
                    "<br/>";
                pin.label +=
                    "<h2>" +
                    stop.StopDetails[0].Description +
                    " " +
                    restStopIcon +
                    "</h2>";
                pin.content =
                    stop.StopDetails[0].Addresses[addressNumber - 1].Address +
                    "\n " +
                    restStopText;
            } else {
                if (index === stopList.length - 1) {
                    pin.label =
                        "Arrives " +
                        moment(stop.ArrivalDate).format("HH:mm") +
                        "<br/>";
                } else {
                    pin.label =
                        "Departs " +
                        moment(stop.DepartureDate).format("HH:mm") +
                        "<br/>";
                }
                pin.label += "<h2>" + stop.StopDetails[0].Description + "</h2>";
                pin.content =
                    stop.StopDetails[0].Addresses[addressNumber - 1].Address;
            }

            pins.push(pin);
        }

        const service = JSON.parse(this.route);
        service.forEach(addToWaypoints);
        service.forEach(addToMap);

        pins[0].iconUrl = "/assets/icons/map/pin.png";
        pins[pins.length - 1].iconUrl = "/assets/icons/map/pin.png";

        this.markers = pins;
        this.lat = service[0].StopDetails[0].Latitude;
        this.lng = service[0].StopDetails[0].Longitude;
        const routes = chunkArray(service, 20);

        this.dir = [];
        for (let i = 0; i < routes.length; i++) {
            const route = routes[i];

            const waypoints = [];
            for (let x = 0; x < routes[i].length; x++) {
                const stop = routes[i][x];
                const addressNumber = stop.StopDetails[0].AddressNumber;
                const lat =
                    stop.StopDetails[0].Addresses[addressNumber - 1].Latitude;
                const long =
                    stop.StopDetails[0].Addresses[addressNumber - 1].Longitude;
                if (lat !== 0 && long !== 0) {
                    waypoints.push({
                        location: lat + "," + long,
                        stopover: true
                    });
                }
            }
            const originAddressNumber = route[0].StopDetails[0].AddressNumber;
            const destinationAddressNumber =
                route[route.length - 1].StopDetails[0].AddressNumber;
            this.dir.push({
                origin: {
                    lat:
                        route[0].StopDetails[0].Addresses[
                            originAddressNumber - 1
                        ].Latitude,
                    lng:
                        route[0].StopDetails[0].Addresses[
                            originAddressNumber - 1
                        ].Longitude
                },
                destination: {
                    lat:
                        route[route.length - 1].StopDetails[0].Addresses[
                            destinationAddressNumber - 1
                        ].Latitude,
                    lng:
                        route[route.length - 1].StopDetails[0].Addresses[
                            destinationAddressNumber - 1
                        ].Longitude
                },
                waypoints: waypoints
            });

        }

        this.initialised = true;
    }
}
class point implements LatLngBoundsLiteral {
    east: number;
    north: number;
    south: number;
    west: number;
}
export class marker {
    lat: number;
    lng: number;
    label?: string;
    content?: string;
    draggable: boolean;
    iconUrl: string;
}



// WEBPACK FOOTER //
// ./src/app/shared/components/google-map/google-map.component.ts